import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

const pct = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);
const cls = (v) => (v === null || v === undefined ? 'text-slate-500' : v > 0 ? 'text-emerald-300' : v < 0 ? 'text-rose-300' : 'text-slate-300');
const fav = (e, v) => (v === null || v === undefined ? null : e.side === 'LONG' ? v : -v);
const when = (ts) => new Date(ts * 1000).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const MODE_ICON = (m) => (m === 'real' ? '🏦' : '🧪');

function BotCell({ bot, skip }) {
    if (!bot?.length) {
        const ks = Object.entries(skip || {});
        if (!ks.length) return <span className="text-slate-500">açmadı</span>;
        return (
            <span className="text-slate-400" data-testid="pump-history-skip" title="Bot adayı gördü ama açmadı — neden">
                açmadı: {ks.map(([m, v]) => `${MODE_ICON(m)} ${v.reason}`).join(' · ')}
            </span>
        );
    }
    return (
        <span className="space-x-1">
            {bot.map((p, i) => (
                <span key={i} className={`font-mono ${p.status === 'closed' ? cls(p.pnl_usd) : 'text-sky-200'}`}>
                    {MODE_ICON(p.mode)} {p.status === 'closed' ? `${Number(p.pnl_usd || 0) > 0 ? '+' : ''}${Number(p.pnl_usd || 0).toFixed(2)}$ (${p.close_reason || 'kapandı'})` : p.status === 'open' ? 'açık' : p.status}
                </span>
            ))}
        </span>
    );
}

/** Son 7 günün patlama adayları: 1 sa / 24 sa sonra ne oldu, zirve, bot ne yaptı. */
export default function PumpHistoryPanel({ open }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        const load = () => apiClient.get('/autotrade/pump/history', { params: { days: 7 } }).then(({ data: d }) => alive && setData(d)).catch((e) => alive && setErr(e?.response?.data?.detail || 'yüklenemedi'));
        load();
        const id = setInterval(load, 60000);
        return () => { alive = false; clearInterval(id); };
    }, [open]);
    if (!open) return null;
    if (err) return <div className="text-[11px] text-rose-300" data-testid="pump-history-error">Patlama geçmişi {err}</div>;
    if (!data) return <div className="text-[11px] text-slate-400" data-testid="pump-history-loading">Yükleniyor…</div>;
    return (
        <div className="space-y-2" data-testid="pump-history">
            <div className="text-[11px] text-slate-400" data-testid="pump-history-summary">
                Son {data.days} gün: <b className="text-slate-100">{data.n}</b> aday · 1 sa sonra yönünde devam: <b className="text-slate-100">{data.cont_1h_pct === null ? '—' : `%${data.cont_1h_pct}`}</b> ({data.n_1h} ölçüldü)
                · 24 sa: <b className="text-slate-100">{data.cont_24h_pct === null ? '—' : `%${data.cont_24h_pct}`}</b> ({data.n_24h}) · ort. zirve <b className="text-slate-100">{data.avg_peak_pct === null ? '—' : `+%${data.avg_peak_pct}`}</b>
                · 🆕 {data.new_listings} yeni listelenen · bot {data.bot_trades} işlem
            </div>
            {data.n === 0 ? (
                <div className="text-[11px] text-slate-500" data-testid="pump-history-empty">Henüz patlama adayı kaydedilmedi — radar ilk adayı bulunca burada 1 sa / 24 sa takibiyle listelenir.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-[11px]" data-testid="pump-history-table">
                        <thead className="text-[10px] uppercase tracking-wide text-slate-500">
                            <tr className="text-left">
                                <th className="py-1 pr-2">Zaman</th><th className="pr-2">Coin</th><th className="pr-2">Yön</th><th className="pr-2">5 dk</th><th className="pr-2">Hacim</th><th className="pr-2">Skor</th>
                                <th className="pr-2">1 sa sonra</th><th className="pr-2">24 sa sonra</th><th className="pr-2">Zirve</th><th>Bot</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.events.map((e) => (
                                <tr key={`${e.symbol}-${e.first_ts}`} className="border-t border-slate-800/70" data-testid={`pump-history-${e.symbol}-${Math.round(e.first_ts)}`}>
                                    <td className="py-1 pr-2 font-mono text-slate-400">{when(e.first_ts)}</td>
                                    <td className="pr-2 font-bold text-slate-100">{e.symbol.replace('USDT', '')} {e.stage === 'warm' && <span className="text-orange-300" title="ısınma (patlama öncesi)">🔥</span>}{e.jump && <span className="text-yellow-300" title="sıçrama anında yakalandı (1 dk)">⚡</span>}{e.new_listing && <span className="text-sky-300">🆕</span>}{e.late && <span className="text-amber-300" title="geç kalındı"> ⏱</span>}</td>
                                    <td className={`pr-2 font-bold ${e.side === 'LONG' ? 'text-emerald-300' : 'text-rose-300'}`}>{e.side === 'LONG' ? '▲ LONG' : '▼ SHORT'}</td>
                                    <td className={`pr-2 font-mono ${cls(e.chg_5m)}`}>{pct(e.chg_5m)}</td>
                                    <td className="pr-2 font-mono text-slate-200">{Number(e.vol_ratio).toFixed(0)}×</td>
                                    <td className="pr-2 font-mono text-slate-200">{e.score}</td>
                                    <td className={`pr-2 font-mono ${cls(fav(e, e.after_1h_pct))}`}>{pct(e.after_1h_pct)}</td>
                                    <td className={`pr-2 font-mono ${cls(fav(e, e.after_24h_pct))}`}>{pct(e.after_24h_pct)}</td>
                                    <td className="pr-2 font-mono text-emerald-200">{e.peak_pct ? `+${Number(e.peak_pct).toFixed(1)}%` : '—'}</td>
                                    <td><BotCell bot={e.bot} skip={e.bot_skip} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
